import resolveConfig from "tailwindcss/resolveConfig";
import type { Config } from "tailwindcss";
import * as tailwindConfigModule from "../../tailwind.config.js";

type BreakpointRange = {
    currentBreakpoint: Breakpoint;
    start?: Breakpoint;
    end?: Breakpoint;
};

export class BreakpointUtils {
    private static readonly tailwindConfig = (tailwindConfigModule as unknown as { default: Config }).default;
    private static readonly fullConfig = resolveConfig(this.tailwindConfig);

    // Define the order explicitly
    private static readonly breakpointOrder = ["base", "2xs", "xs", "sm", "md", "lg", "xl", "2xl"] as const;

    // Create breakpoints object with guaranteed order
    static readonly breakpoints = this.breakpointOrder.reduce(
        (acc, key) => {
            if (key === "base") {
                acc[key] = "0px";
            } else if (key === "2xs") {
                acc[key] = "360px";
            } else if (key === "xs") {
                acc[key] = "428px";
            } else {
                acc[key] = this.fullConfig.theme?.screens?.[key] || "";
            }
            return acc;
        },
        {} as Record<(typeof this.breakpointOrder)[number], string>
    );

    // Export the type based on the breakpoints object
    static readonly type = {} as { [K in keyof typeof BreakpointUtils.breakpoints]: K };

    // Preset ranges for common breakpoint groups
    static readonly Ranges = {
        mobile: { start: "base" as const, end: "xs" as const },
        tablet: { start: "sm" as const, end: "lg" as const },
        desktop: { start: "xl" as const, end: "2xl" as const }
    } as const;

    /**
     * Checks if the current breakpoint matches exactly
     */
    static is(currentBreakpoint: Breakpoint, targetBreakpoint: Breakpoint): boolean {
        return currentBreakpoint === targetBreakpoint;
    }

    /**
     * Checks if the current breakpoint is within a given range (inclusive)
     * If start is omitted, starts from "base"
     * If end is omitted, ends at "2xl"
     */
    static isWithin({ currentBreakpoint, start, end }: BreakpointRange): boolean {
        const startIndex = start ? this.breakpointOrder.indexOf(start) : 0;
        const endIndex = end ? this.breakpointOrder.indexOf(end) : this.breakpointOrder.length - 1;
        const currentIndex = this.breakpointOrder.indexOf(currentBreakpoint);

        return currentIndex >= startIndex && currentIndex <= endIndex;
    }

    /**
     * Checks if the current breakpoint is within a given set of breakpoints
     */
    static isWithinSet(currentBreakpoint: Breakpoint, breakpoints: Breakpoint[]): boolean {
        return breakpoints.includes(currentBreakpoint);
    }

    /**
     * Gets all breakpoints within a range (inclusive)
     * If start is omitted, starts from "base"
     * If end is omitted, ends at "2xl"
     */
    static getRange({ start, end }: { start?: Breakpoint; end?: Breakpoint }): Breakpoint[] {
        const startIndex = start ? this.breakpointOrder.indexOf(start) : 0;
        const endIndex = end ? this.breakpointOrder.indexOf(end) : this.breakpointOrder.length - 1;

        return this.breakpointOrder.slice(startIndex, endIndex + 1) as Breakpoint[];
    }

    /**
     * Gets value from a breakpoint map, using the current breakpoint
     */
    static getValue<T>(currentBreakpoint: Breakpoint, breakpointMap: Record<Breakpoint, T>): T {
        return breakpointMap[currentBreakpoint];
    }

    /**
     * Checks if the current breakpoint is within the mobile range
     */
    static isMobile(currentBreakpoint: Breakpoint): boolean {
        return this.isWithin({
            currentBreakpoint,
            ...this.Ranges.mobile
        });
    }

    /**
     * Checks if the current breakpoint is within the tablet range
     */
    static isTablet(currentBreakpoint: Breakpoint): boolean {
        return this.isWithin({
            currentBreakpoint,
            ...this.Ranges.tablet
        });
    }

    /**
     * Checks if the current breakpoint is within the desktop range
     */
    static isDesktop(currentBreakpoint: Breakpoint): boolean {
        return this.isWithin({
            currentBreakpoint,
            ...this.Ranges.desktop
        });
    }
}

// Export the Breakpoint type
export type Breakpoint = keyof typeof BreakpointUtils.type;
